import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, Users2, ListChecks, RefreshCw, UserPlus, Mail as MailIcon, Loader2,
  CheckCircle2, Crown, Briefcase, X as XIcon, AlertTriangle, Search,
  FileText, Calendar as CalIcon, User as UserIcon, Mail,
} from "lucide-react";
import { PlacetelCallButton } from "@/components/PlacetelCallButton";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import TelesalesCapture from "@/pages/TelesalesCapture";
import SubmissionDetailModal from "@/components/SubmissionDetailModal";

const PACKAGE_LABEL = { guenstig: "Günstig", komfort: "Komfort", premium: "Premium" };
const STATUS_LABEL = {
  pending_review: "Direktor-Freigabe",
  new: "Neu",
  archived: "Archiviert",
  sent_back: "Zurück an Agent",
  routed: "An Vertrieb",
  in_auction: "Auktion",
  assigned: "Zugewiesen",
};
const STATUS_COLOR = {
  pending_review: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  new: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  archived: "bg-zinc-700/40 text-zinc-400 border-zinc-700",
  sent_back: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  routed: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40",
  in_auction: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  assigned: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40",
};

export default function TelesalesHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const role = user?.role;
  const isDirector = role === "telesales_director";
  const isAdmin = role === "admin";

  const tabs = useMemo(() => {
    if (isDirector || isAdmin) {
      return [
        { key: "capture", label: "Erfassen", icon: Phone },
        { key: "team", label: "Mein Team", icon: Users2 },
        { key: "submissions", label: "Erfassungen", icon: ListChecks },
        { key: "offers", label: "Orientierungsangebote", icon: FileText },
      ];
    }
    return [
      { key: "capture", label: "Erfassen", icon: Phone },
      { key: "submissions", label: "Meine Erfassungen", icon: ListChecks },
      { key: "offers", label: "Orientierungsangebote", icon: FileText },
    ];
  }, [isDirector, isAdmin]);

  const initialTab = params.get("tab") || "capture";
  const [tab, setTab] = useState(
    tabs.some((t) => t.key === initialTab) ? initialTab : "capture",
  );

  const switchTab = (k) => {
    setTab(k);
    const next = new URLSearchParams(params);
    next.set("tab", k);
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="telesales-home">
      <AppHeader title="Telesales" back="/dashboard" />
      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-2xl bg-[#00E575]/15 flex items-center justify-center">
            {isDirector ? <Crown className="text-amber-400" size={20} /> : <Phone className="text-[#00E575]" size={20} />}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-xl sm:text-2xl font-bold">
              {isDirector ? "Telesales-Direktor" : isAdmin ? "Telesales (Admin)" : "Telesales-Agent"}
            </h1>
            <p className="text-[12px] text-zinc-400 leading-snug font-medium">
              {isDirector
                ? "Eigene Erfassung, Team-Übersicht, alle Erfassungen und versendeten Orientierungsangebote Ihres Teams an einem Ort."
                : isAdmin
                  ? "Erfassung, alle Telesales-Agents Ihres Mandanten und sämtliche Erfassungen + Orientierungsangebote."
                  : "Telefonate erfassen, eigene Erfassungen und versendete Orientierungsangebote einsehen."}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 sm:gap-2 mb-4 overflow-x-auto pb-1" data-testid="telesales-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => switchTab(t.key)}
                className={`shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-[13px] font-bold transition ${
                  active
                    ? "bg-[#00E575] text-[#09090B]"
                    : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-zinc-800"
                }`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "capture" && (
          <section data-testid="tab-capture-content" className="bg-[#18181B] border border-zinc-800 rounded-2xl p-3 sm:p-5">
            <TelesalesCapture embedded={true} />
          </section>
        )}

        {tab === "submissions" && <SubmissionsTab navigate={navigate} />}

        {tab === "offers" && <OrientationOffersTab />}

        {(tab === "team") && (isDirector || isAdmin) && <TeamTab isDirector={isDirector} />}
      </main>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Submissions Tab — scoped server-side
// ---------------------------------------------------------------------------
function SubmissionsTab({ navigate }) {
  const { user } = useAuth();
  const role = user?.role;
  const canAssign = ["admin", "super_admin", "sales_director", "sales_manager", "telesales_director"].includes(role)
    || user?.is_super_admin;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [activeId, setActiveId] = useState(null);
  const [salesReps, setSalesReps] = useState([]);
  const [inlineAssignId, setInlineAssignId] = useState("");  // currently open assign-dropdown
  const [inlineAssignTo, setInlineAssignTo] = useState("");
  const [inlineAssigning, setInlineAssigning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/telesales/submissions?limit=300");
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!canAssign) return;
    api.get("/users").then((r) => {
      const reps = (r.data || []).filter(
        (u) => ["sales_rep", "sales_manager", "sales_director"].includes(u.role) &&
               u.status !== "deleted",
      );
      setSalesReps(reps);
    }).catch(() => {});
  }, [canAssign]);

  const submitInlineAssign = async (leadId) => {
    if (!inlineAssignTo) return;
    setInlineAssigning(true);
    try {
      const r = await api.post(
        `/telesales/submissions/${leadId}/assign`,
        { assigned_user_id: inlineAssignTo },
      );
      const a = r.data.assignee || {};
      const conv = r.data.conversion || {};
      toast.success(
        conv.created
          ? `Zugewiesen an ${a.first_name} ${a.last_name} · Angebot ${conv.offer_number}`
          : `Zugewiesen an ${a.first_name} ${a.last_name}`,
      );
      setInlineAssignId("");
      setInlineAssignTo("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen");
    } finally { setInlineAssigning(false); }
  };

  const filtered = useMemo(() => {
    let arr = items;
    if (filter !== "all") arr = arr.filter((x) => (x.status || "new") === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((x) =>
        [x.first_name, x.last_name, x.email, x.phone, x.city, x.agent_name, x.agent_email]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(q)),
      );
    }
    return arr;
  }, [items, filter, search]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    items.forEach((i) => set.add(i.status || "new"));
    return Array.from(set);
  }, [items]);

  return (
    <section data-testid="tab-submissions-content">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-3 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche: Name, E-Mail, Telefon, Stadt, Agent…"
            data-testid="submissions-search"
            className="h-10 w-full bg-[#09090B] border border-zinc-800 rounded-lg pl-9 pr-3 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="submissions-filter-status"
          className="h-10 bg-[#09090B] border border-zinc-800 rounded-lg px-3 text-[13px] text-zinc-200"
        >
          <option value="all">Alle Status ({items.length})</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
          ))}
        </select>
        <button
          onClick={load}
          data-testid="submissions-refresh"
          className="h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 text-[12px] inline-flex items-center gap-1.5 font-medium"
        >
          <RefreshCw size={14} /> Neu laden
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="animate-spin inline text-zinc-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-10 text-center" data-testid="submissions-empty">
          <ListChecks className="mx-auto text-zinc-600 mb-3" size={36} />
          <h3 className="font-bold text-[15px] mb-1">Noch keine Erfassungen</h3>
          <p className="text-zinc-400 text-[12px] font-medium">
            Sobald ein Lead erfasst wird, erscheint er hier.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="submissions-list">
          {filtered.map((l) => (
            <div
              key={l.id}
              data-testid={`submission-${l.id}`}
              className="bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 rounded-2xl p-4 transition-colors"
            >
              <button
                type="button"
                onClick={() => setActiveId(l.id)}
                className="w-full text-left"
                data-testid={`submission-${l.id}-open`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest border ${
                        STATUS_COLOR[l.status || "new"] || "bg-zinc-700/40 text-zinc-300 border-zinc-700"
                      }`}>
                        {STATUS_LABEL[l.status || "new"] || l.status || "—"}
                      </span>
                      {l.agent_name && (
                        <span className="text-[11.5px] text-zinc-300 inline-flex items-center gap-1 font-medium">
                          <Briefcase size={11} /> {l.agent_name}
                        </span>
                      )}
                      <span className="text-[11.5px] text-zinc-400">
                        {l.received_at && new Date(l.received_at).toLocaleString("de-DE")}
                      </span>
                    </div>
                    <div className="font-bold text-[14px]">
                      {l.salutation ? `${l.salutation} ` : ""}{l.first_name} {l.last_name}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[12px] text-zinc-300 font-medium">
                      {l.email && <span>{l.email}</span>}
                      {l.phone && <span className="font-mono">{l.phone}</span>}
                      {(l.zip || l.city) && <span>{l.zip} {l.city}</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11.5px] text-zinc-300 font-medium">
                      <span>{l.current_heating} • {PACKAGE_LABEL[l.package] || l.package}</span>
                      <span>{l.appointment_date} {l.appointment_time}</span>
                      {l.orientation_offer?.estimated_heat_load_kw && (
                        <span className="text-[#00E575]">{l.orientation_offer.estimated_heat_load_kw} kW</span>
                      )}
                      {l.offer_number && (
                        <span className="text-[#00E575] font-mono font-bold">{l.offer_number}</span>
                      )}
                    </div>
                  </div>
                  <span className="shrink-0 text-[12px] text-zinc-300 uppercase tracking-widest font-semibold">
                    Bearbeiten →
                  </span>
                </div>
              </button>

              {/* Inline Zuweisen-Quick-Action für Manager+ */}
              {canAssign && (
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  {inlineAssignId === l.id ? (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <select
                        value={inlineAssignTo}
                        onChange={(e) => setInlineAssignTo(e.target.value)}
                        className="h-9 flex-1 bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 text-[12.5px] focus:border-[#00E575] outline-none"
                        data-testid={`inline-assign-picker-${l.id}`}
                      >
                        <option value="">— Sales-Rep —</option>
                        {salesReps.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.first_name} {u.last_name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => submitInlineAssign(l.id)}
                        disabled={!inlineAssignTo || inlineAssigning}
                        data-testid={`inline-assign-submit-${l.id}`}
                        className="h-9 px-4 rounded-lg bg-[#00E575] text-zinc-950 font-bold text-[12.5px] hover:bg-[#00C964] disabled:opacity-40 inline-flex items-center gap-1.5 whitespace-nowrap"
                      >
                        {inlineAssigning ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                        Zuweisen
                      </button>
                      <button
                        type="button"
                        onClick={() => { setInlineAssignId(""); setInlineAssignTo(""); }}
                        className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[12.5px]"
                      >
                        Abbrechen
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setInlineAssignId(l.id); setInlineAssignTo(l.assigned_to_user_id || ""); }}
                      data-testid={`inline-assign-open-${l.id}`}
                      className="text-[12px] text-[#00E575] hover:text-[#00C964] font-semibold inline-flex items-center gap-1.5"
                    >
                      <UserPlus size={13} />
                      {l.assigned_to_user_id ? "Neu zuweisen" : "An Sales-Rep zuweisen"}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {activeId && (
        <SubmissionDetailModal
          submissionId={activeId}
          onClose={() => setActiveId(null)}
          onSaved={() => load()}
        />
      )}
    </section>
  );
}


// ---------------------------------------------------------------------------
// Orientierungsangebote Tab — zeigt Erfassungen mit berechnetem Angebot,
// sortierbar nach Datum und Name
// ---------------------------------------------------------------------------
function OrientationOffersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");  // date_desc | date_asc | name_asc | name_desc
  const [activeId, setActiveId] = useState(null);
  const [followUpFor, setFollowUpFor] = useState(null);  // {id, name, currentWv, currentNote}
  const [pdfLoadingId, setPdfLoadingId] = useState("");  // `${id}:${slot}`

  const viewPdf = async (leadId, slot) => {
    const key = `${leadId}:${slot}`;
    setPdfLoadingId(key);
    try {
      const r = await api.get(
        `/telesales/submissions/${leadId}/orientation-pdf`,
        { params: { slot }, responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `Orientierungsangebot_${slot}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      let msg = "PDF konnte nicht erzeugt werden";
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* ignore */ }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally { setPdfLoadingId(""); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/telesales/submissions?limit=500");
      const withOffer = (data || []).filter(
        (l) => l.orientation_offer && l.orientation_offer.estimated_heat_load_kw,
      );
      setItems(withOffer);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = items;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((x) =>
        [x.first_name, x.last_name, x.email, x.phone, x.city, x.agent_name]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(q)),
      );
    }
    const sorters = {
      date_desc: (a, b) => (b.received_at || "").localeCompare(a.received_at || ""),
      date_asc:  (a, b) => (a.received_at || "").localeCompare(b.received_at || ""),
      name_asc:  (a, b) => `${a.last_name || ""} ${a.first_name || ""}`
        .localeCompare(`${b.last_name || ""} ${b.first_name || ""}`, "de"),
      name_desc: (a, b) => `${b.last_name || ""} ${b.first_name || ""}`
        .localeCompare(`${a.last_name || ""} ${a.first_name || ""}`, "de"),
    };
    return [...arr].sort(sorters[sortBy] || sorters.date_desc);
  }, [items, search, sortBy]);

  // Conversion-Statistik: wie viele Orientierungsangebote landen in der Pipeline?
  // Pipeline = lead wurde an Vertrieb übergeben (routed, assigned, in_auction, won, lost)
  const stats = useMemo(() => {
    const total = items.length;
    const inPipeline = items.filter((x) =>
      ["routed", "assigned", "in_auction", "won", "lost"].includes(x.status),
    ).length;
    const won = items.filter((x) => x.status === "won").length;
    const wiedervorlage = items.filter((x) => x.status === "wiedervorlage_geplant" || x.wiedervorlage_at).length;
    const rate = total > 0 ? Math.round((inPipeline / total) * 100) : 0;
    const winRate = total > 0 ? Math.round((won / total) * 100) : 0;
    return { total, inPipeline, won, wiedervorlage, rate, winRate };
  }, [items]);

  return (
    <section data-testid="tab-offers-content">
      {/* Conversion-Statistik */}
      {!loading && stats.total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="offers-stats">
          <StatCard
            label="Versendet"
            value={stats.total}
            sub="Orientierungsangebote"
            tone="zinc"
            testId="stat-total"
          />
          <StatCard
            label="In Pipeline"
            value={`${stats.inPipeline}`}
            sub={`${stats.rate}% Conversion`}
            tone="emerald"
            testId="stat-pipeline"
          />
          <StatCard
            label="Gewonnen"
            value={`${stats.won}`}
            sub={`${stats.winRate}% Quote`}
            tone="amber"
            testId="stat-won"
          />
          <StatCard
            label="Wiedervorlage"
            value={`${stats.wiedervorlage}`}
            sub="Nachfass geplant"
            tone="sky"
            testId="stat-wv"
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-3 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche: Name, E-Mail, Telefon, Stadt…"
            data-testid="offers-search"
            className="h-10 w-full bg-[#09090B] border border-zinc-800 rounded-lg pl-9 pr-3 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="offers-sort"
          className="h-10 bg-[#09090B] border border-zinc-800 rounded-lg px-3 text-[13px] text-zinc-200"
        >
          <option value="date_desc">Datum ↓ (neueste zuerst)</option>
          <option value="date_asc">Datum ↑ (älteste zuerst)</option>
          <option value="name_asc">Name A → Z</option>
          <option value="name_desc">Name Z → A</option>
        </select>
        <button
          onClick={load}
          data-testid="offers-refresh"
          className="h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 text-[12px] inline-flex items-center gap-1.5 font-medium"
        >
          <RefreshCw size={14} /> Neu laden
        </button>
      </div>

      <div className="text-[12px] text-zinc-300 mb-2 px-1">
        {filtered.length} {filtered.length === 1 ? "Orientierungsangebot" : "Orientierungsangebote"} versendet
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="animate-spin inline text-zinc-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-10 text-center" data-testid="offers-empty">
          <FileText className="mx-auto text-zinc-600 mb-3" size={36} />
          <h3 className="font-bold text-[15px] mb-1">Noch keine Orientierungsangebote</h3>
          <p className="text-zinc-400 text-[12px] font-medium">
            Sobald Sie einen Lead erfassen und das System ein passendes Angebot berechnet, erscheint es hier.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="offers-list">
          {filtered.map((l) => {
            const oo = l.orientation_offer || {};
            const pkg = oo.package || {};
            const kfwPct = oo.kfw?.rate_pct;
            const verkauf = pkg.verkaufspreis_brutto;
            return (
              <div
                key={l.id}
                data-testid={`offer-${l.id}`}
                className="relative bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 rounded-2xl transition-colors"
              >
                <button
                  type="button"
                  onClick={() => setActiveId(l.id)}
                  className="w-full text-left p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40">
                          <CalIcon size={10} className="inline mr-1" />
                          {l.received_at && new Date(l.received_at).toLocaleDateString("de-DE")}
                        </span>
                        {l.agent_name && (
                          <span className="text-[12px] text-zinc-300 inline-flex items-center gap-1">
                            <Briefcase size={10} /> {l.agent_name}
                          </span>
                        )}
                        {l.wiedervorlage_at && (
                          <span className="px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest bg-amber-500/15 text-amber-300 border border-amber-500/40">
                            Wiedervorlage {new Date(l.wiedervorlage_at).toLocaleDateString("de-DE")}
                          </span>
                        )}
                        {/* Mail-Status-Badge: Latest result aus email_delivery / last_email_delivery */}
                        {(() => {
                          const latest = l.last_email_delivery || l.email_delivery;
                          if (!latest) return null;
                          if (latest.sent) {
                            return (
                              <span
                                data-testid={`offer-mail-ok-${l.id}`}
                                className="px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 inline-flex items-center gap-1"
                                title={`Mail versendet an ${l.email || "Kunde"}`}
                              >
                                <Mail size={9} /> Mail OK
                              </span>
                            );
                          }
                          return (
                            <span
                              data-testid={`offer-mail-fail-${l.id}`}
                              className="px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest bg-rose-500/15 text-rose-300 border border-rose-500/40 inline-flex items-center gap-1"
                              title={`Mail fehlgeschlagen: ${latest.reason || latest.error || "—"}`}
                            >
                              <Mail size={9} /> Mail-Fehler
                            </span>
                          );
                        })()}
                      </div>
                      <div className="font-bold text-[14px]">
                        <UserIcon size={12} className="inline mr-1 text-zinc-500" />
                        {l.salutation ? `${l.salutation} ` : ""}{l.first_name} {l.last_name}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[12px] text-zinc-300 font-medium">
                        {(l.zip || l.city) && <span>{l.zip} {l.city}</span>}
                        {l.email && <span>{l.email}</span>}
                        {l.phone && <span className="font-mono">{l.phone}</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2 text-[12px] font-medium">
                        <span className="font-bold text-zinc-100">{pkg.name || "—"}</span>
                        <span className="text-[#00E575] font-mono font-bold">{oo.estimated_heat_load_kw} kW</span>
                        {verkauf && (
                          <span className="text-zinc-300 font-mono">
                            {verkauf.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                        )}
                        {kfwPct > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 text-[12px] font-bold">
                            KfW {kfwPct} %
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
                <div className="flex items-center justify-end gap-2 px-4 pb-3 -mt-1 border-t border-zinc-800/40 pt-2.5 flex-wrap">
                  {l.phone && (
                    <PlacetelCallButton phone={l.phone} leadId={l.id} size="sm" variant="full" />
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); viewPdf(l.id, "guenstig"); }}
                    disabled={pdfLoadingId === `${l.id}:guenstig`}
                    data-testid={`offer-pdf-guenstig-${l.id}`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#00E575]/10 hover:bg-[#00E575]/20 text-[#00E575] border border-[#00E575]/40 text-[11.5px] font-bold transition disabled:opacity-50"
                    title="Günstig-Variante als PDF anzeigen (HEIKO Thermal Plus)"
                  >
                    {pdfLoadingId === `${l.id}:guenstig`
                      ? <Loader2 size={12} className="animate-spin" />
                      : <FileText size={12} />}
                    PDF günstig
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); viewPdf(l.id, "komfort_premium"); }}
                    disabled={pdfLoadingId === `${l.id}:komfort_premium`}
                    data-testid={`offer-pdf-komfort-${l.id}`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#00E575]/10 hover:bg-[#00E575]/20 text-[#00E575] border border-[#00E575]/40 text-[11.5px] font-bold transition disabled:opacity-50"
                    title="Premium-Variante als PDF anzeigen (LG Therma V R290)"
                  >
                    {pdfLoadingId === `${l.id}:komfort_premium`
                      ? <Loader2 size={12} className="animate-spin" />
                      : <FileText size={12} />}
                    PDF Premium
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFollowUpFor({
                        id: l.id,
                        name: `${l.first_name || ""} ${l.last_name || ""}`.trim(),
                        currentWv: l.wiedervorlage_at || "",
                        currentNote: l.wiedervorlage_note || "",
                      });
                    }}
                    data-testid={`offer-followup-${l.id}`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[11.5px] font-bold transition"
                  >
                    <CalIcon size={12} /> Nachfassen
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setActiveId(l.id); }}
                    className="text-[12px] text-zinc-300 uppercase tracking-widest hover:text-zinc-300"
                  >
                    Details →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {activeId && (
        <SubmissionDetailModal
          submissionId={activeId}
          onClose={() => setActiveId(null)}
          onSaved={() => load()}
        />
      )}
      {followUpFor && (
        <FollowUpModal
          target={followUpFor}
          onClose={() => setFollowUpFor(null)}
          onSaved={() => { setFollowUpFor(null); load(); }}
        />
      )}
    </section>
  );
}


// ---------------------------------------------------------------------------
// Nachfassen-Modal — voreingestelltes Datum +7 Tage, individuell anpassbar
// ---------------------------------------------------------------------------
function FollowUpModal({ target, onClose, onSaved }) {
  const defaultDateTime = useMemo(() => {
    if (target.currentWv) {
      const d = new Date(target.currentWv);
      return {
        date: d.toISOString().slice(0, 10),
        time: d.toTimeString().slice(0, 5),
      };
    }
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(10, 0, 0, 0);
    return { date: d.toISOString().slice(0, 10), time: "10:00" };
  }, [target.currentWv]);

  const [date, setDate] = useState(defaultDateTime.date);
  const [time, setTime] = useState(defaultDateTime.time);
  const [note, setNote] = useState(
    target.currentNote
      || `Nachfass zu Orientierungsangebot${target.name ? ` für ${target.name}` : ""}.`,
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!date || !time) {
      toast.error("Datum und Uhrzeit angeben");
      return;
    }
    setSaving(true);
    try {
      const local = new Date(`${date}T${time}:00`);
      if (isNaN(local.getTime())) {
        toast.error("Ungültiges Datum");
        setSaving(false);
        return;
      }
      await api.patch(`/telesales/submissions/${target.id}`, {
        wiedervorlage_at: local.toISOString(),
        wiedervorlage_note: note || null,
      });
      toast.success("Nachfass-Termin gespeichert", {
        description: `${new Date(local).toLocaleDateString("de-DE")} um ${time} Uhr`,
      });
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await api.patch(`/telesales/submissions/${target.id}`, {
        clear_wiedervorlage: true,
      });
      toast.success("Nachfass-Termin entfernt");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-3"
      onClick={() => !saving && onClose()}
      data-testid="followup-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#18181B] border-t sm:border border-zinc-800 sm:rounded-2xl"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <h3 className="font-bold inline-flex items-center gap-2 text-amber-300">
            <CalIcon size={16} /> Nachfassen
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded" data-testid="followup-close">
            <XIcon size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[12.5px] text-zinc-400">
            Wiedervorlage für <strong className="text-zinc-100">{target.name || "diesen Kontakt"}</strong>.
            Voreinstellung: <strong className="text-amber-300">in 7 Tagen um 10:00 Uhr</strong> — kann beliebig angepasst werden.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Datum</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="followup-date"
                min={new Date().toISOString().slice(0, 10)}
                className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-amber-400 outline-none text-[13px]"
              />
            </div>
            <div>
              <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Uhrzeit</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="followup-time"
                className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-amber-400 outline-none text-[13px]"
              />
            </div>
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Notiz</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="followup-note"
              rows={3}
              className="w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 focus:border-amber-400 outline-none text-[13px] resize-none"
              placeholder="z.B. Nachfass nach Orientierungsangebot, Förderung klären…"
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
            {target.currentWv && (
              <button
                type="button"
                onClick={handleClear}
                disabled={saving}
                data-testid="followup-clear"
                className="h-11 px-4 rounded-xl bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 text-zinc-300 text-[12px] font-bold"
              >
                Termin entfernen
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              data-testid="followup-save"
              className="flex-1 h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-[#09090B] font-bold inline-flex items-center justify-center gap-2 disabled:bg-zinc-800 disabled:text-zinc-500"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
              {saving ? "Speichere…" : "Nachfass-Termin speichern"}
            </button>
          </div>
          <p className="text-[10.5px] text-zinc-500 leading-snug pt-1 border-t border-zinc-800/60 mt-1">
            5 Minuten vor der Wiedervorlage erhalten Sie automatisch eine Web-Push-Benachrichtigung (falls in Ihrem Profil aktiviert).
          </p>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Team Tab — director invites + lists their managed agents
// ---------------------------------------------------------------------------
function TeamTab({ isDirector }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/telesales/team");
      setMembers(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <section data-testid="tab-team-content">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-[12px] text-zinc-300 font-medium">
          {isDirector
            ? "Ihre Telesales-Agents. Neu eingeladene Agents tauchen hier auf, sobald sie den Magic-Link akzeptiert haben."
            : "Alle Telesales-Agents im Mandanten."}
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="team-refresh"
            className="h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 text-[12px] inline-flex items-center gap-1.5 font-medium"
          >
            <RefreshCw size={14} /> Neu laden
          </button>
          {isDirector && (
            <button
              onClick={() => setInviteOpen(true)}
              data-testid="team-invite-btn"
              className="h-10 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964]"
            >
              <UserPlus size={14} /> Agent einladen
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="animate-spin inline text-zinc-500" /></div>
      ) : members.length === 0 ? (
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-10 text-center" data-testid="team-empty">
          <Users2 className="mx-auto text-zinc-600 mb-3" size={36} />
          <h3 className="font-bold text-[15px] mb-1">Noch keine Agents im Team</h3>
          <p className="text-zinc-400 text-[12px] mb-4 font-medium">
            Laden Sie Ihren ersten Telesales-Agent per E-Mail-Magic-Link ein.
          </p>
          {isDirector && (
            <button
              onClick={() => setInviteOpen(true)}
              className="h-10 px-4 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964]"
            >
              <UserPlus size={14} /> Ersten Agent einladen
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="team-list">
          {members.map((m) => (
            <div
              key={m.id}
              data-testid={`team-member-${m.id}`}
              className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-[14px] truncate">
                    {m.first_name} {m.last_name}
                  </div>
                  <div className="text-[12px] text-zinc-400 truncate">{m.email}</div>
                  {m.phone && <div className="text-[12px] text-zinc-300 font-mono">{m.phone}</div>}
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest ${
                  m.status === "active" ? "bg-[#00E575]/15 text-[#00E575]" : "bg-amber-500/15 text-amber-300"
                }`}>
                  {m.status === "active" ? "Aktiv" : m.status}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-3 text-[12px] text-zinc-400">
                <span data-testid={`team-member-count-${m.id}`}>
                  <strong className="text-zinc-100">{m.submission_count || 0}</strong> Erfassungen
                </span>
                {m.last_submission_at && (
                  <span>letzte: {new Date(m.last_submission_at).toLocaleDateString("de-DE")}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {inviteOpen && (
        <InviteAgentModal
          onClose={() => setInviteOpen(false)}
          onSaved={() => { setInviteOpen(false); load(); }}
        />
      )}
    </section>
  );
}


// ---------------------------------------------------------------------------
// Invite-Agent Modal (director-specific, restricted to role=agent)
// ---------------------------------------------------------------------------
function InviteAgentModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", target_email: "", target_phone: "",
  });
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState("");

  const submit = async () => {
    if (!form.target_email && !form.target_phone) {
      toast.error("E-Mail oder Telefon angeben");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/invitations", {
        role: "agent",
        first_name: form.first_name,
        last_name: form.last_name,
        target_email: form.target_email || undefined,
        target_phone: form.target_phone || undefined,
      });
      setLink(data.invite_url);
      toast.success(data.delivery?.email?.ok
        ? "Einladung per E-Mail versendet"
        : "Einladung erstellt — Link unten");
      // do not auto-close; user can copy the link
      setTimeout(onSaved, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Einladung fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-3"
      onClick={() => !saving && onClose()}
      data-testid="invite-agent-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#18181B] border-t sm:border border-zinc-800 sm:rounded-2xl"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <h3 className="font-bold inline-flex items-center gap-2">
            <UserPlus size={16} /> Telesales-Agent einladen
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded">
            <XIcon size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[12px] text-zinc-300 font-medium">
            Der Agent erhält einen Magic-Link per E-Mail und richtet sein Konto selbst ein.
            Er wird automatisch Ihrem Team zugeordnet.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Vorname</label>
              <input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                data-testid="invite-agent-first"
                className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"
              />
            </div>
            <div>
              <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Nachname</label>
              <input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                data-testid="invite-agent-last"
                className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"
              />
            </div>
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">E-Mail (Empfänger des Links)</label>
            <input
              type="email"
              value={form.target_email}
              onChange={(e) => setForm({ ...form, target_email: e.target.value })}
              data-testid="invite-agent-email"
              placeholder="agent@firma.de"
              className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"
            />
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Telefon (optional)</label>
            <input
              type="tel"
              value={form.target_phone}
              onChange={(e) => setForm({ ...form, target_phone: e.target.value })}
              data-testid="invite-agent-phone"
              placeholder="+4915XXXXXXXX"
              className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"
            />
            {form.target_phone && (
              <div className="mt-1.5 p-2 rounded-lg bg-amber-500/5 border border-amber-500/30 text-[10.5px] text-amber-200 leading-snug flex gap-1.5">
                <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                <span>
                  Hinweis: SMS-Versand ist derzeit im Twilio-Trial-Modus
                  (nur vorverifizierte Nummern). Der Magic-Link wird bevorzugt
                  per E-Mail zugestellt.
                </span>
              </div>
            )}
          </div>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="invite-agent-submit"
            className="w-full h-11 rounded-xl bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 disabled:bg-zinc-800 disabled:text-zinc-500"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <MailIcon size={14} />}
            {saving ? "Sende…" : "Einladung senden"}
          </button>
          {link && (
            <div className="p-3 rounded-lg bg-[#00E575]/5 border border-[#00E575]/30" data-testid="invite-agent-link">
              <div className="flex items-center gap-1.5 text-[12px] uppercase tracking-widest text-[#00E575] font-bold mb-1">
                <CheckCircle2 size={11} /> Einladungs-Link
              </div>
              <input
                readOnly
                value={link}
                onFocus={(e) => e.target.select()}
                className="w-full bg-[#09090B] border border-zinc-700 rounded px-2 py-1.5 text-[12px] text-zinc-300 font-mono"
              />
              <p className="text-[12px] text-zinc-300 mt-1">
                Können Sie auch manuell kopieren und z.B. via WhatsApp senden.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// StatCard — kleine KPI-Kachel für den Conversion-Banner
// ---------------------------------------------------------------------------
function StatCard({ label, value, sub, tone = "zinc", testId }) {
  const tones = {
    zinc:    { border: "border-zinc-800",        bg: "bg-[#18181B]",        accent: "text-zinc-200",   subAccent: "text-zinc-500" },
    emerald: { border: "border-[#00E575]/40",    bg: "bg-[#00E575]/10",     accent: "text-[#00E575]",  subAccent: "text-[#00E575]/80" },
    amber:   { border: "border-amber-500/40",    bg: "bg-amber-500/10",     accent: "text-amber-300",  subAccent: "text-amber-300/80" },
    sky:     { border: "border-sky-500/40",      bg: "bg-sky-500/10",       accent: "text-sky-300",    subAccent: "text-sky-300/80" },
  };
  const t = tones[tone] || tones.zinc;
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border ${t.border} ${t.bg} px-3 py-2.5`}
    >
      <div className="text-[10.5px] uppercase tracking-widest text-zinc-200 font-bold">{label}</div>
      <div className={`text-[20px] font-bold leading-tight font-mono ${t.accent}`}>{value}</div>
      <div className={`text-[12px] ${t.subAccent}`}>{sub}</div>
    </div>
  );
}
